/**
 * Axis-aligned box used when placing the event popover away from other UI.
 */
export interface PopoverRect {
  /** Left edge, in viewport pixels */
  x: number
  /** Top edge, in viewport pixels */
  y: number
  /** Width in pixels */
  width: number
  /** Height in pixels */
  height: number
}

/**
 * Options for shifting a popover off overlapping UI.
 */
export interface ShiftPopoverOptions {
  /** Gap kept between the popover and an obstacle */
  gap?: number
  /** Minimum inset from the viewport edges */
  viewportPadding?: number
  /** Viewport width used to decide whether the left side still fits */
  viewportWidth?: number
}

/**
 * Whether two rectangles share any interior pixels.
 * @param left - First rectangle
 * @param right - Second rectangle
 */
export function popoverRectsOverlap(left: PopoverRect, right: PopoverRect): boolean {
  return (
    left.x < right.x + right.width &&
    left.x + left.width > right.x &&
    left.y < right.y + right.height &&
    left.y + left.height > right.y
  )
}

/**
 * Moves `floating` so it no longer intersects any obstacle, preferring the left side.
 * @param floating - Proposed popover box
 * @param obstacles - Panels the popover must not cover (overflow list, day cell, …)
 * @param options - Gap and viewport limits
 */
export function shiftPopoverAwayFromObstacles(
  floating: PopoverRect,
  obstacles: PopoverRect[],
  options: ShiftPopoverOptions = {}
): Pick<PopoverRect, 'x' | 'y'> {
  const gap = options.gap ?? 12
  const padding = options.viewportPadding ?? 8
  const viewportWidth = options.viewportWidth ?? 0
  let x = floating.x
  const y = floating.y

  obstacles.forEach(obstacle => {
    const current = { x, y, width: floating.width, height: floating.height }

    // Already clear of this obstacle
    if (!popoverRectsOverlap(current, obstacle)) {
      return
    }

    const leftX = obstacle.x - floating.width - gap
    const rightX = obstacle.x + obstacle.width + gap
    const leftFits = viewportWidth <= 0 || leftX >= padding
    const rightFits = viewportWidth <= 0 || rightX + floating.width <= viewportWidth - padding

    // Prefer the left so a right-edge trigger does not cover the events beside it
    if (leftFits) {
      x = leftX
      return
    }

    if (rightFits) {
      x = rightX
      return
    }

    // Both sides overflow the viewport; keep as much of the card on-screen as possible
    x = padding
  })

  return { x, y }
}
